import { gql } from "@apollo/client";
import { Button, useContentEditor } from "@resource/atlas";
import { createValueSet } from "@resource/atlas/content-editor/variables";
import { useLogEvent } from "analytics";
import { GuideAboutEditor } from "client/components/guide-content";
import {
  UpdateOrganizationDefaultGuideContentTemplate,
  UpdateOrganizationDefaultGuideContentTemplateVariables,
} from "generated/schemaTypes";
import { ComponentPropsWithoutRef, useEffect, useMemo, useState } from "react";
import { GuideAboutVariableSet } from "shared/guide-content/about";
import useMutation from "utils/useMutation";

export type GuideContentProps = ComponentPropsWithoutRef<"div"> & {
  content?: string;
  organizationName?: string;
  disabled?: boolean;
};

const UPDATE_ORGANIZATION_DEFAULT_GUIDE_CONTENT = gql`
  mutation UpdateOrganizationDefaultGuideContentTemplate(
    $input: UpdateOrganizationDefaultGuideContentTemplateInput!
  ) {
    updateOrganizationDefaultGuideContentTemplate(input: $input) {
      success
      message
    }
  }
`;

export function GuideContent({
  content,
  organizationName,
  disabled,
  ...props
}: GuideContentProps) {
  const { editor, contentEditorProps } = useContentEditor();
  const [updateDefaultGuideContent, { loading }] = useMutation<
    UpdateOrganizationDefaultGuideContentTemplate,
    UpdateOrganizationDefaultGuideContentTemplateVariables
  >(UPDATE_ORGANIZATION_DEFAULT_GUIDE_CONTENT);

  const [contentChanged, setContentChanged] = useState(false);

  const logEvent = useLogEvent({
    component: "GuideContent",
    project: "Zeus",
  });

  useEffect(() => {
    if (content) {
      editor.setEditorState(editor.parseEditorState(content));
      setContentChanged(false);
    }
  }, [content, editor]);

  const saveContent = async () => {
    const data = editor.getEditorState().toJSON();

    logEvent("Save Button Clicked");

    await updateDefaultGuideContent({
      variables: {
        input: {
          data,
        },
      },
    });
  };

  const valueSet = useMemo(() => createValueSet<GuideAboutVariableSet>({}), []);

  return (
    <div {...props} className="lg:flex gap-6">
      <div>
        <h4 className="text-h4 mb-2">Company overview</h4>
        <div className="text-body-md text-subtle">
          Share details about your company such as your mission, the team, press
          highlights, and more. This information will be shared by default with
          candidates in their guides.
        </div>
        {disabled && (
          <div className="text-body-md mt-2 text-blue-500">
            Only an admin can customize this information.
          </div>
        )}
      </div>
      <div>
        <div className="h-[37.5rem] lg:w-[30rem] xl:w-[40rem] rounded-md shadow-1 py-2 px-6 my-2 overflow-hidden">
          <GuideAboutEditor
            className="h-full"
            valueSet={valueSet}
            {...contentEditorProps}
            onChange={(...editorProps) => {
              contentEditorProps?.onChange?.(...editorProps);
              setContentChanged(true);
            }}
          />
        </div>
        <div className="flex justify-end">
          <Button
            variant="primary"
            onClick={saveContent}
            isLoading={loading}
            disabled={!contentChanged || disabled}
          >
            Save changes
          </Button>
        </div>
      </div>
    </div>
  );
}
